/**
 * The numbered grid (SPEC §2.3 phase 1, DESIGN.md §3.2). The number-pick ritual is
 * inherited straight from the physical game, so the grid is a board, not a form
 * control: every number is present, used ones are burned out permanently, and
 * the grey-out is meaningful precisely because the room's deck ships exactly
 * `max` questions (§3.1).
 *
 * `max` is the *room's* deck size, not a constant: safe mode plays a smaller,
 * separately-numbered deck, and a board that showed 201 numbers for an
 * 88-question deck would be exactly the lie the grey-out exists to rule out.
 *
 * It owns its own scroller rather than riding the screen's, because the deck
 * asks for a position indicator pinned to the right edge *of the grid* — and a
 * bar that tracks something it does not contain is a bar that drifts.
 */
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { DECK_SIZE, QUESTION_NUMBER_MIN } from '@idgame/protocol';

export interface NumberGridProps {
  /** Greyed out, unpickable, and stay burned across "play again" (SPEC §2.6). */
  used: readonly number[];
  /** The highest number on this room's board — `deckSizeFor(settings.deckId)`. */
  max?: number;
  /** Absent for spectators — the grid renders read-only. */
  onSelect?: (n: number) => void;
  disabled?: boolean;
  /** The tap-selected number. Committing is the sticky action's job, not ours. */
  selected?: number | null;
}

/** Thumb geometry, in per-cent of the track. */
interface Thumb {
  top: number;
  height: number;
}

export function NumberGrid({
  used,
  max = DECK_SIZE,
  onSelect,
  disabled = false,
  selected = null,
}: NumberGridProps) {
  const burned = new Set(used);
  const numbers: number[] = [];
  for (let n = QUESTION_NUMBER_MIN; n <= max; n += 1) numbers.push(n);

  const scroller = useRef<HTMLDivElement>(null);
  const [thumb, setThumb] = useState<Thumb>({ top: 0, height: 100 });

  const measure = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const overflow = scrollHeight - clientHeight;
    const height = overflow > 0 ? Math.max(12, (clientHeight / scrollHeight) * 100) : 100;
    const top = overflow > 0 ? (scrollTop / overflow) * (100 - height) : 0;
    setThumb({ top, height });
  }, []);

  useLayoutEffect(measure, [measure]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="relative flex min-h-0 flex-1">
        <div
          ref={scroller}
          onScroll={measure}
          style={{ scrollbarWidth: 'none' }}
          className="min-h-0 flex-1 overflow-y-auto pr-3"
        >
          <div
            data-testid="number-grid"
            className="grid grid-cols-5 gap-2 pb-1"
            role="group"
            aria-label={`Pick a question number from ${QUESTION_NUMBER_MIN} to ${max}`}
          >
            {numbers.map((n) => {
              const isUsed = burned.has(n);
              const isSelected = selected === n;
              return (
                <button
                  key={n}
                  type="button"
                  data-testid={`number-${n}`}
                  data-used={isUsed ? 'true' : 'false'}
                  aria-label={isUsed ? `${n}, already used` : `${n}`}
                  aria-pressed={isUsed ? undefined : isSelected}
                  aria-disabled={isUsed || undefined}
                  disabled={isUsed || disabled || !onSelect}
                  onClick={() => onSelect?.(n)}
                  className={[
                    'flex h-14 flex-col items-center justify-center rounded-card text-base',
                    'font-semibold tabular-nums transition',
                    isUsed
                      ? 'border border-transparent bg-disabled text-ink-soft/45'
                      : isSelected
                        ? // A darker terracotta edge on a terracotta fill: the
                          // selection reads as one solid object, not a ring.
                          'border-2 border-[#b8512c] bg-terracotta text-cream'
                        : 'border border-line bg-cream text-ink active:scale-95',
                  ].join(' ')}
                >
                  <span className="leading-none">{n}</span>
                  {isUsed && (
                    <span className="mt-1 text-[0.5rem] font-semibold tracking-[0.12em] uppercase">
                      Used
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        <div
          aria-hidden
          data-testid="grid-position"
          className="absolute top-0 right-0 h-full w-[3px] overflow-hidden rounded-full bg-line/40"
        >
          <div
            className="absolute inset-x-0 rounded-full bg-green/70"
            style={{ top: `${thumb.top}%`, height: `${thumb.height}%` }}
          />
        </div>
      </div>
    </div>
  );
}
